import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  deleteAnnouncementAction,
  fetchAnnouncementByIdAction,
  fetchAnnouncementsAction,
  updateAnnouncementStatusAction,
} from "../../ustils/actioncreator";
import { useAdminUi } from "../../hooks/useAdminUi";
import AnnouncementDetailPanel from "./AnnouncementDetailPanel";

function extractAnnouncementList(res) {
  if (!res || typeof res !== "object") return [];
  if (Array.isArray(res.data)) return res.data;
  if (res.data && typeof res.data === "object" && Array.isArray(res.data.data)) return res.data.data;
  if (Array.isArray(res.announcements)) return res.announcements;
  return [];
}

function extractTotalRecords(res, listLength) {
  const n = Number(res?.totalRecords);
  return Number.isFinite(n) && n >= 0 ? n : listLength;
}

function truncateLocationDisplay(value, maxLen = 25) {
  const s = value == null ? "" : String(value);
  return s.length > maxLen ? `${s.slice(0, maxLen)}...` : s;
}

function unwrapAnnouncementDetail(res) {
  if (!res || typeof res !== "object") return null;
  if (res._id) return res;
  const d = res.data;
  if (d && typeof d === "object" && !Array.isArray(d) && (d._id || d.id)) return d;
  return null;
}

function formatPropertyPrice(price, currency) {
  if (price == null || price === "") return "—";
  const n = Number(price);
  if (!Number.isFinite(n)) return String(price);
  const code = currency && String(currency).trim();
  if (code) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `${n.toLocaleString()} ${code}`;
    }
  }
  return n.toLocaleString();
}

function formatAnnouncementStatus(item) {
  const s = item?.status;
  /** API: 1 = pending, 2 = active, 3 = rejected */
  if (s === 1 || s === "1") return "Pending";
  if (s === 2 || s === "2") return "Active";
  if (s === 3 || s === "3") return "Rejected";
  if (s === 0 || s === "0") return "Draft";
  if (typeof s === "number" || (typeof s === "string" && s !== "")) return `Status ${s}`;
  if (item?.verificationStatus != null) return String(item.verificationStatus);
  return "—";
}

function mapAnnouncementRow(item) {
  const id = item?._id || item?.id;
  const u = item?.user_id;
  const owner = typeof u === "object" && u !== null ? u : null;
  const ownerName = owner?.name || item?.user?.name || item?.user_name || item?.broker_name || "—";
  const ownerEmail = owner?.email || item?.user?.email || "";
  const userAvatar = owner?.userProfileImage || null;
  const title =
    item?.property_name || item?.title || item?.propertyTitle || item?.name || "—";
  const subtitle = ownerEmail ? `${ownerName} · ${ownerEmail}` : ownerName;
  let type = item?.property_type || item?.type || item?.category || item?.dealing_area || "—";
  if (item?.is_commercial_property === true) type = `${type} · Commercial`;
  const location =
    item?.property_address ||
    [item?.property_area, item?.property_city, item?.property_country].filter(Boolean).join(", ") ||
    (typeof item?.property_location === "object" && Array.isArray(item.property_location?.coordinates)
      ? item.property_location.coordinates.join(", ")
      : null) ||
    item?.address ||
    item?.location ||
    item?.city ||
    "—";
  const price =
    item?.price != null && item?.price !== ""
      ? formatPropertyPrice(item.price, item?.currency)
      : item?.formatted_price != null
        ? String(item.formatted_price)
        : "—";
  const status = formatAnnouncementStatus(item);
  const dateRaw = item?.created_at || item?.createdAt || item?.listed_at;
  const dateListed = dateRaw
    ? new Date(dateRaw).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";
  const isWishlisted =
    item?.is_wishlisted === true ? "Yes" : item?.is_wishlisted === false ? "No" : "—";
  return { id, title, subtitle, userAvatar, type, location, price, status, dateListed, isWishlisted, raw: item };
}

export default function AnnouncementsList({ fetchStatus = 1, listTitle }) {
  const {
    ui,
    listHeaderSx,
    listPaperSx,
    listTitleSx,
    primaryBtnSx,
    statusChipSx,
    tableBodyCellSx,
    tableHeaderCellSx,
    tableRowHoverSx,
    tableSx,
    tableContainerSx,
  } = useAdminUi();
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [userRole, setUserRole] = useState(1);

  const showModerationActions = Number(fetchStatus) === 1;
  const heading =
    listTitle ??
    (Number(fetchStatus) === 2 ? "Active announcements" : "Announcement approval");
  const emptyMessage =
    Number(fetchStatus) === 2 ? "No active announcements found." : "No announcements pending approval.";

  const roleFilterButtonSx = (active) => ({
    textTransform: "none",
    fontSize: 13,
    minWidth: 96,
    px: 1.5,
    py: 0.6,
    borderRadius: 1.5,
    ...(active
      ? {
          bgcolor: ui.green,
          color: "#fff",
          borderColor: ui.green,
          "&:hover": { bgcolor: ui.greenDark || ui.green },
        }
      : {
          color: ui.inkMuted,
          borderColor: ui.borderLight,
          "&:hover": { borderColor: ui.green, color: ui.green },
        }),
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchAnnouncementsAction({
        page,
        perPage,
        status: fetchStatus,
        user_role: userRole,
      });
      const list = extractAnnouncementList(res);
      setRows(list.map(mapAnnouncementRow));
      setTotal(extractTotalRecords(res, list.length));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load announcements.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, fetchStatus, userRole]);

  const handleRoleFilterChange = (role) => {
    if (role === userRole) return;
    setUserRole(role);
    setPage(1);
  };

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / perPage) || 1);
  const canGoNext = page < totalPages && rows.length > 0;

  const openDetail = async (id) => {
    if (!id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailRecord(null);
    setDetailError("");
    try {
      const raw = await fetchAnnouncementByIdAction(id);
      setDetailRecord(unwrapAnnouncementDetail(raw) || raw?.data || null);
    } catch (e) {
      setDetailError(e?.response?.data?.message || e?.message || "Failed to load details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRecord(null);
    setDetailError("");
  };

  const handleApprove = async (announcementId) => {
    if (!announcementId) return;
    setActionError("");
    setBusyId(announcementId);
    try {
      const out = await updateAnnouncementStatusAction({
        announcement_id: announcementId,
        status: 2,
      });
      if (out?.success === false) {
        setActionError(out?.message || "Approve failed.");
        return;
      }
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.message || e?.message || "Approve failed.");
    } finally {
      setBusyId(null);
    }
  };

  const openReject = (id) => {
    setRejectTargetId(id);
    setRejectReason("");
    setActionError("");
    setRejectOpen(true);
  };

  const closeReject = () => {
    setRejectOpen(false);
    setRejectTargetId(null);
    setRejectReason("");
    setActionError("");
  };

  const submitReject = async () => {
    if (!rejectTargetId) return;
    const reason = rejectReason.trim();
    if (!reason) {
      setActionError("Please enter a rejection reason.");
      return;
    }
    setBusyId(rejectTargetId);
    try {
      const out = await updateAnnouncementStatusAction({
        announcement_id: rejectTargetId,
        status: 3,
        rejection_reason: reason,
      });
      if (out?.success === false) {
        setActionError(out?.message || "Reject failed.");
        return;
      }
      closeReject();
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.message || e?.message || "Reject failed.");
    } finally {
      setBusyId(null);
    }
  };

  const openDelete = (id) => {
    setDeleteTargetId(id);
    setDeleteOpen(true);
    setActionError("");
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setBusyId(deleteTargetId);
    try {
      await deleteAnnouncementAction(deleteTargetId);
      setDeleteOpen(false);
      setDeleteTargetId(null);
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.message || e?.message || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper elevation={0} sx={listPaperSx}>
      <Box
        sx={{
          ...listHeaderSx,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
          <Typography sx={listTitleSx}>{heading}</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={userRole === 1 ? "contained" : "outlined"}
              onClick={() => handleRoleFilterChange(1)}
              sx={roleFilterButtonSx(userRole === 1)}
            >
              User
            </Button>
            <Button
              size="small"
              variant={userRole === 2 ? "contained" : "outlined"}
              onClick={() => handleRoleFilterChange(2)}
              sx={roleFilterButtonSx(userRole === 2)}
            >
              Broker
            </Button>
          </Stack>
        </Stack>
        <Button variant="contained" color="primary" sx={primaryBtnSx}>
          + Add Announcements
        </Button>
      </Box>
      {error ? (
        <Alert severity="error" sx={{ m: 1 }}>
          {error}
        </Alert>
      ) : null}
      {actionError && !rejectOpen && !detailOpen ? (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderCellSx}>Property & User</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Type</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Property Location</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Price</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Status</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Wishlisted</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Date Listed</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ ...tableBodyCellSx, textAlign: "center" }}>
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow hover key={r.id || r.title} sx={tableRowHoverSx}>
                      <TableCell sx={tableBodyCellSx}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Avatar
                            src={r.userAvatar || undefined}
                            sx={{ width: 32, height: 32, fontSize: 12, mt: 0.1, flexShrink: 0 }}
                          >
                            {(r.subtitle || r.title || "?")[0]}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{r.title}</Typography>
                            <Typography sx={{ fontSize: 14, color: ui.inkSoft, wordBreak: "break-word" }}>
                              {r.subtitle}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.type}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{truncateLocationDisplay(r.location)}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.price}</TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Chip
                          label={r.status}
                          size="small"
                          sx={{ height: 26, fontSize: 14, ...statusChipSx(r.status) }}
                        />
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        {r.isWishlisted !== "—" ? (
                          <Chip
                            label={r.isWishlisted}
                            size="small"
                            sx={{
                              height: 26,
                              fontSize: 13,
                              ...statusChipSx(r.isWishlisted === "Yes" ? "Active" : "Inactive"),
                            }}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.dateListed}</TableCell>
                      <TableCell
                        sx={{
                          ...tableBodyCellSx,
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <RemoveRedEyeOutlinedIcon
                            sx={{ fontSize: 18, color: ui.iconView, cursor: "pointer", flexShrink: 0 }}
                            onClick={() => r.id && openDetail(r.id)}
                          />
                          {showModerationActions ? (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={busyId === r.id}
                                onClick={() => r.id && handleApprove(r.id)}
                            color="primary"
                            sx={{
                              textTransform: "none",
                              fontSize: 14,
                              minWidth: 0,
                              px: 0.8,
                              flexShrink: 0,
                            }}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={busyId === r.id}
                                onClick={() => r.id && openReject(r.id)}
                                sx={{ textTransform: "none", fontSize: 14, minWidth: 0, px: 0.8, flexShrink: 0 }}
                              >
                                Reject
                              </Button>
                            </>
                          ) : null}
                          <DeleteOutlineOutlinedIcon
                            sx={{ fontSize: 18, color: ui.iconDelete, cursor: "pointer", flexShrink: 0 }}
                            onClick={() => r.id && openDelete(r.id)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent={{ xs: "center", sm: "flex-end" }} flexWrap="wrap" spacing={1} sx={{ p: 1.2 }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Typography sx={{ fontSize: 14, alignSelf: "center", color: ui.inkMuted }}>
              Page {page} {total ? `(${total} total)` : ""}
            </Typography>
            <Button size="small" disabled={!canGoNext} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </Stack>
        </>
      )}

      <Dialog
        open={detailOpen}
        onClose={() => !detailLoading && closeDetail()}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, maxHeight: { xs: "90vh", sm: "85vh" } }}>
          {detailError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {detailError}
            </Alert>
          ) : null}
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : detailRecord ? (
            <AnnouncementDetailPanel announcement={detailRecord} />
          ) : (
            <Typography sx={{ fontSize: 12, color: ui.inkSoft }}>No detail payload.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: "divider" }}>
          <Button onClick={closeDetail} variant="contained" color="primary" sx={{ textTransform: "none", minWidth: 100 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !busyId && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete announcement</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>This cannot be undone. Continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={!!busyId}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={!!busyId}>
            {busyId ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => !busyId && closeReject()} fullWidth maxWidth="xs">
        <DialogTitle>Reject announcement</DialogTitle>
        <DialogContent>
          {actionError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {actionError}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            margin="dense"
            label="Rejection reason"
            fullWidth
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReject} disabled={!!busyId}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={submitReject} disabled={!!busyId}>
            {busyId ? <CircularProgress size={18} color="inherit" /> : "Submit rejection"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
